import { StockItem } from '../../../shared-utilities/models-old/datastructures';
import { AccessCodeBaseEnum } from '../../user-access/models/access-code.enum';
import {
  DisableRuleSupplierActionEnum,
  IDisableStockItemRule,
  IEnableStockItemRule,
  IOnHandDisableRule,
} from '../../settings-new/models';
import { hasAccessToRule } from '../../../shared/shared-utils/user-access/user-access.utils';
import { ReportUtils } from '../../../shared-utilities/utils-old/shared-utils-old/report-utils';

const AUDIT_COMPARE_FIELDS: (keyof StockItem)[] = [
  'desc',
  'lineColourCode',
  'onHoldCode',
  'regularSuppCode',
];

/**
 * True when the user can edit stock and submit those edits for the store.
 */
export function userCanProcessStockDisableAudit(accessCode: number | undefined): boolean {
  const code = accessCode ?? 0;
  return (
    hasAccessToRule(code, AccessCodeBaseEnum.StockEditAccess) &&
    hasAccessToRule(code, AccessCodeBaseEnum.StockEditSubmit)
  );
}

/**
 * True when the rule would change a stock item, rather than leaving every field at its default.
 */
export function isDisableRuleConfigured(rule: IOnHandDisableRule | undefined): boolean {
  if (!rule) {
    return false;
  }
  if (rule.descPrefix?.trim() || rule.descSuffix?.trim()) {
    return true;
  }
  if (Number(rule.lineColour ?? 0) !== 0 || Number(rule.onHoldCode ?? 0) !== 0) {
    return true;
  }
  return supplierCriterion(rule) !== 'none';
}

/**
 * Splits stock into the disable-audit checks.
 * Rule mismatches are tagged disabled items that do not match the rule for their current on-hand.
 * Partial descriptions have that rule's prefix or suffix, are not disabled, and miss the rest of the rule.
 * Missing tags are items that already match the rule for their on-hand, without the disabled tag.
 */
export function classifyStockDisableAudit(
  items: StockItem[],
  rules: IDisableStockItemRule,
  enableRules: IEnableStockItemRule,
): {
  disabledRuleMismatches: StockItem[];
  missingDisableTag: StockItem[];
  partialDisableDescriptions: StockItem[];
} {
  const disabledRuleMismatches: StockItem[] = [];
  const missingDisableTag: StockItem[] = [];
  const partialDisableDescriptions: StockItem[] = [];

  items.forEach((item: StockItem) => {
    if (buildDisabledTagRuleUpdate(item, rules, enableRules)) {
      disabledRuleMismatches.push(item);
      return;
    }
    if (buildPartialDisableDescriptionUpdate(item, rules, enableRules)) {
      partialDisableDescriptions.push(item);
      return;
    }
    if (isMissingDisableTag(item, rules)) {
      missingDisableTag.push(item);
    }
  });

  return { disabledRuleMismatches, missingDisableTag, partialDisableDescriptions };
}

/**
 * Copies a disabled item and applies the disable rule for its current on-hand.
 * Strips the other rule's description text first, so a zero-on-hand item loses the not-zero wording.
 * Returns null when that rule is not configured, the tag is missing, or the item already matches.
 */
export function buildDisabledTagRuleUpdate(
  item: StockItem,
  rules: IDisableStockItemRule,
  enableRules: IEnableStockItemRule,
): StockItem | null {
  if (!itemHasDisabledTag(item)) {
    return null;
  }
  return applyDisableRuleForOnHand(item, rules, enableRules, true);
}

/**
 * Copies an item that already has the disable prefix or suffix and applies the rest of that rule.
 * Does not add the disabled tag. Returns null when the description marker is absent or nothing changes.
 */
export function buildPartialDisableDescriptionUpdate(
  item: StockItem,
  rules: IDisableStockItemRule,
  enableRules: IEnableStockItemRule,
): StockItem | null {
  if (itemHasDisabledTag(item)) {
    return null;
  }
  const applicable = Number(item.onHand ?? 0) === 0 ? rules.onHandZero : rules.onHandNotZero;
  if (!itemHasDescriptionMarker(item, applicable)) {
    return null;
  }
  return applyDisableRuleForOnHand(item, rules, enableRules, false);
}

/**
 * Applies the disable rule for the item's current on-hand after removing the other rule's description text.
 */
function applyDisableRuleForOnHand(
  item: StockItem,
  rules: IDisableStockItemRule,
  enableRules: IEnableStockItemRule,
  keepDisabledTag: boolean,
): StockItem | null {
  const onHandIsZero = Number(item.onHand ?? 0) === 0;
  const applicable = onHandIsZero ? rules.onHandZero : rules.onHandNotZero;
  const other = onHandIsZero ? rules.onHandNotZero : rules.onHandZero;
  if (!isDisableRuleConfigured(applicable) || itemMatchesDisableRule(item, applicable)) {
    return null;
  }

  const next: StockItem = ensureDisableAuditOriginalValue(structuredClone(item));
  next.desc = stripRuleText(next.desc, other);
  ReportUtils.enableReport(next, rules, enableRules ?? ({} as IEnableStockItemRule), false);
  if (keepDisabledTag) {
    next.disabled = true;
    if (!itemHasDisabledTag(next)) {
      next._tags = [...(next._tags ?? []), 'disabled'];
    }
  }
  next.isEdited = true;

  const changed = AUDIT_COMPARE_FIELDS.some(
    (field: keyof StockItem) => !sameAuditValue(item[field], next[field]),
  );
  return changed ? next : null;
}

/**
 * True when the description already starts with the rule prefix or ends with the rule suffix.
 */
function itemHasDescriptionMarker(item: StockItem, rule: IOnHandDisableRule | undefined): boolean {
  if (!rule) {
    return false;
  }
  const description = String(item.desc ?? '').trim();
  const prefix = rule.descPrefix?.trim() ?? '';
  const suffix = rule.descSuffix?.trim() ?? '';
  if (prefix && description.startsWith(prefix)) {
    return true;
  }
  return !!suffix && description.endsWith(suffix);
}

function isMissingDisableTag(item: StockItem, rules: IDisableStockItemRule): boolean {
  if (itemHasDisabledTag(item)) {
    return false;
  }
  const rule = Number(item.onHand ?? 0) === 0 ? rules.onHandZero : rules.onHandNotZero;
  return isDisableRuleConfigured(rule) && itemMatchesDisableRule(item, rule);
}

function itemHasDisabledTag(item: StockItem): boolean {
  return Array.isArray(item._tags) && item._tags.map(String).includes('disabled');
}

/**
 * True when every configured field on the rule matches the item.
 * Empty description text is ignored. Line colour and on-hold are included once the rule is configured.
 */
function itemMatchesDisableRule(item: StockItem, rule: IOnHandDisableRule | undefined): boolean {
  if (!rule || !isDisableRuleConfigured(rule)) {
    return false;
  }

  const description = String(item.desc ?? '').trim();
  const prefix = rule.descPrefix?.trim() ?? '';
  const suffix = rule.descSuffix?.trim() ?? '';
  if (prefix && !description.startsWith(prefix)) {
    return false;
  }
  if (suffix && !description.endsWith(suffix)) {
    return false;
  }
  if (Number(item.lineColourCode ?? 0) !== Number(rule.lineColour ?? 0)) {
    return false;
  }
  if (Number(item.onHoldCode ?? 0) !== Number(rule.onHoldCode ?? 0)) {
    return false;
  }

  const supplier = supplierCriterion(rule);
  const supplierCode = String(item.regularSuppCode ?? '').trim();
  if (supplier === 'remove' && supplierCode !== '') {
    return false;
  }
  if (supplier === 'set' && supplierCode !== String(rule.regularSuppCode ?? '').trim()) {
    return false;
  }
  return true;
}

function supplierCriterion(rule: IOnHandDisableRule): 'none' | 'remove' | 'set' {
  if (rule.supplierAction === DisableRuleSupplierActionEnum.remove) {
    return 'remove';
  }
  if (rule.supplierAction === DisableRuleSupplierActionEnum.set && rule.regularSuppCode?.trim()) {
    return 'set';
  }
  return 'none';
}

function ensureDisableAuditOriginalValue(item: StockItem): StockItem {
  const originalValue = { ...(item.originalValue ?? {}) };
  const ensure = (field: string, value: StockItem[keyof StockItem]): void => {
    if (!originalValue[field]) {
      originalValue[field] = { value: (value ?? 0) as string };
    }
  };
  ensure('desc', item.desc ?? '');
  ensure('lineColourCode', item.lineColourCode ?? 0);
  ensure('onHoldCode', item.onHoldCode ?? 0);
  ensure('regularSuppCode', item.regularSuppCode ?? '');
  ensure('_tags', item._tags ?? []);
  return { ...item, originalValue };
}

function stripRuleText(
  description: string | undefined,
  rule: IOnHandDisableRule | undefined,
): string {
  let text = description ?? '';
  if (rule?.descPrefix?.trim()) {
    text = ReportUtils.stripPrefixes(text, [rule.descPrefix]);
  }
  if (rule?.descSuffix?.trim()) {
    text = ReportUtils.stripSuffixes(text, [rule.descSuffix]);
  }
  return text;
}

function sameAuditValue(
  left: StockItem[keyof StockItem],
  right: StockItem[keyof StockItem],
): boolean {
  if (typeof left === 'number' || typeof right === 'number') {
    return Number(left ?? 0) === Number(right ?? 0);
  }
  return String(left ?? '') === String(right ?? '');
}
